import { Platform } from 'react-native';

/**
 * Motion tokens for the segmentation pipeline UX.
 *
 * Every transition/animation in the new scan + review flow picks
 * exactly one of these four durations. No ad-hoc milliseconds,
 * no hand-rolled easings. When a designer says "make it snappier",
 * the conversation is "which token?" — not "how many ms?".
 *
 * `prefers-reduced-motion: reduce` collapses all four to an 80ms
 * opacity fade via {@link motionFor}. We never disable transitions
 * wholesale — a hard swap is more jarring than a short fade.
 *
 * See `docs/SEGMENTATION_PIPELINE_PRD.md` §8.14 for the contract.
 */

export const MotionToken = {
  /** 150 ms — button press, badge flash-on-appear, bbox flicker. */
  FLASH: 'flash',
  /** 250 ms with slight overshoot — card pulse, focus glow, IoU badge pop. */
  POP: 'pop',
  /** 400 ms — mask clip-path reveal, card slide-in, bbox diff crossfade. */
  REVEAL: 'reveal',
  /** 800 ms — primary-photo fly-in, confetti, scan-sweep loop. */
  DRIFT: 'drift',
} as const;

export type MotionTokenName = (typeof MotionToken)[keyof typeof MotionToken];

interface MotionSpec {
  /** Duration in milliseconds. */
  durationMs: number;
  /** CSS `cubic-bezier()` easing. Native runtime converts via Animated.Easing. */
  easing: string;
}

const SPECS: Record<MotionTokenName, MotionSpec> = {
  flash: { durationMs: 150, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
  pop: { durationMs: 250, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
  reveal: { durationMs: 400, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  drift: { durationMs: 800, easing: 'ease-in-out' },
};

/**
 * Lightweight cache of the reduced-motion check. The CSS media query
 * rarely changes mid-session; we update the cache on
 * `change` events. Callers treat `prefersReducedMotion()` as a
 * synchronous read for convenience.
 */
let reducedMotion = false;
let reducedMotionWired = false;

const wireReducedMotionListener = () => {
  if (reducedMotionWired) return;
  reducedMotionWired = true;
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  if (!mq) return;
  reducedMotion = mq.matches;
  // `addEventListener('change', …)` is the modern path; `addListener`
  // is the Safari <14 fallback.
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', (e) => {
      reducedMotion = e.matches;
    });
  } else if (typeof (mq as any).addListener === 'function') {
    (mq as any).addListener((e: MediaQueryListEvent) => {
      reducedMotion = e.matches;
    });
  }
};

export const prefersReducedMotion = (): boolean => {
  wireReducedMotionListener();
  return reducedMotion;
};

/**
 * Resolve the active spec for a motion token, honouring the user's
 * reduced-motion preference.
 *
 * Under reduce: all four tokens map to an 80ms opacity fade.
 * Without reduce: tokens return their designed spec.
 *
 * Callers use this directly:
 *   const { durationMs, easing } = motionFor('reveal');
 *   maskEl.style.transition = `clip-path ${durationMs}ms ${easing}`;
 */
export const motionFor = (token: MotionTokenName): MotionSpec => {
  if (prefersReducedMotion()) {
    return { durationMs: 80, easing: 'linear' };
  }
  return SPECS[token];
};

/**
 * Shorthand for a CSS `transition` value. Stable across renders so
 * React's prop-diffing doesn't thrash.
 */
export const transitionFor = (
  property: string,
  token: MotionTokenName,
): string => {
  const { durationMs, easing } = motionFor(token);
  return `${property} ${durationMs}ms ${easing}`;
};
